import { useEffect, useMemo, useRef, useState } from 'react';
import { CpmButton, CpmPageShell } from '../../components/design-system';
import { useApiClient } from '../../hooks/useApiClient';
import { retryBatchJob } from '../../services/productMasterService';
import { useAppDispatch } from '../../store';
import {
    fetchOverviewStats,
    markJobRetried,
    setConfirmingJobId,
    toggleAttentionExpanded,
    toggleExciseInfo,
    toggleTenantInfo,
    useCpmOverview,
} from '../../store/slices/cpmOverviewSlice';
import { toast } from '../../utils/toast';
import { CPM_ATTENTION_TYPE } from '../../constants/centralProductMaster.constants';
import AttentionStrip from './components/AttentionStrip';
import BatchJobHealthPanel from './components/BatchJobHealthPanel';
import CatalogHealthCard from './components/CatalogHealthCard';
import ExciseReadinessPanel from './components/ExciseReadinessPanel';
import Hero, { type PipelineBadge } from './components/Hero';
import LinkDecisionsPanel from './components/LinkDecisionsPanel';
import QuickLinksGrid from './components/QuickLinksGrid';
import SectionError from './components/SectionError';
import TenantLinkagePanel from './components/TenantLinkagePanel';

/** Design: “Data as of today, 09:40 IST” from a wall-clock fetch timestamp. */
function formatAsOfTime(ms: number): string {
    return new Date(ms).toLocaleString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZoneName: 'short',
    });
}

function formatAsOfLabel(ms: number | null): string {
    if (ms == null) return 'Data as of today';
    return `Data as of today, ${formatAsOfTime(ms)}`;
}

function BrandIcon() {
    return (
        <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
        >
            <path d="M4 22h16" />
            <path d="M8 2h8l-1 6a5 5 0 0 1-6 0z" />
            <path d="M9 8v6a3 3 0 0 0 6 0V8" />
            <path d="M12 17v5" />
        </svg>
    );
}

function ProductIcon() {
    return (
        <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
        >
            <path d="m7.5 4.27 9 5.15" />
            <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
            <path d="m3.3 7 8.7 5 8.7-5" />
            <path d="M12 22V12" />
        </svg>
    );
}

function VariantIcon() {
    return (
        <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
        >
            <path d="M3 3v16a2 2 0 0 0 2 2h16" />
            <rect x="7" y="8" width="4" height="9" rx="1" />
            <rect x="14" y="4" width="4" height="13" rx="1" />
        </svg>
    );
}

export default function Overview() {
    const dispatch = useAppDispatch();
    const overview = useCpmOverview();
    const { callApi, loading: retrying } = useApiClient({ toast: true });
    const [slowLoad, setSlowLoad] = useState(false);
    const [bootRetrying, setBootRetrying] = useState(false);
    const [sectionRetrying, setSectionRetrying] = useState<string | null>(null);
    /** Manual Refresh only — mount soft-refresh stays silent. */
    const manualRefreshRef = useRef(false);
    const asOfAtManualStartRef = useRef<number | null>(null);

    useEffect(() => {
        // Soft refresh on enter: keep prior stats visible, no toast.
        dispatch(fetchOverviewStats());
    }, [dispatch]);

    useEffect(() => {
        if (!manualRefreshRef.current) return;
        if (overview.loading === 'loading') return;
        manualRefreshRef.current = false;
        const priorAsOf = asOfAtManualStartRef.current;
        asOfAtManualStartRef.current = null;
        if (overview.refreshFailed) {
            const from =
                priorAsOf != null ? formatAsOfTime(priorAsOf) : 'the last successful load';
            toast.error(`Couldn't refresh the dashboard — still showing data from ${from}.`);
        } else if (overview.lastFetchedAt != null) {
            toast.success(
                `Dashboard refreshed — all counts are current as of ${formatAsOfTime(overview.lastFetchedAt)}.`
            );
        } else {
            toast.success('Dashboard refreshed');
        }
    }, [
        overview.loading,
        overview.refreshFailed,
        overview.lastFetchedAt,
    ]);

    useEffect(() => {
        if (overview.loading !== 'loading') {
            setSlowLoad(false);
            return;
        }
        const timer = setTimeout(() => setSlowLoad(true), 5000);
        return () => clearTimeout(timer);
    }, [overview.loading]);

    useEffect(() => {
        if (overview.loading !== 'loading') {
            setBootRetrying(false);
            setSectionRetrying(null);
        }
    }, [overview.loading]);

    const retrySection = (key: string) => {
        setSectionRetrying(key);
        dispatch(fetchOverviewStats());
    };

    const handleManualRefresh = () => {
        if (overview.loading === 'loading') return;
        manualRefreshRef.current = true;
        asOfAtManualStartRef.current = overview.lastFetchedAt;
        dispatch(fetchOverviewStats());
    };

    const stats = overview.stats;

    const stateProductByState = useMemo(() => {
        const map: Record<string, number> = {};
        for (const row of stats?.stateProductExciseCoverage ?? []) {
            map[row.state] = (map[row.state] ?? 0) + (row.count ?? 0);
        }
        return map;
    }, [stats?.stateProductExciseCoverage]);

    const handleConfirmRetry = async (id: string) => {
        const result = await callApi(() => retryBatchJob(id));
        if (result == null) return;
        toast.success('Job requeued — next daily run will pick up unresolved items');
        dispatch(markJobRetried(id));
        dispatch(fetchOverviewStats());
    };

    if (overview.loading === 'loading' && !stats) {
        return (
            <CpmPageShell gap={24} maxWidth={1280} variant="standard">
                <div
                    className="flex items-center text-[var(--cpm-text-secondary)]"
                    style={{ gap: 8, fontSize: 12.5, fontWeight: 600 }}
                >
                    <span>Admin</span>
                    <span className="text-[var(--cpm-text-tertiary)]" style={{ fontWeight: 100 }}>
                        /
                    </span>
                    <span>Catalog</span>
                    <span className="text-[var(--cpm-text-tertiary)]" style={{ fontWeight: 100 }}>
                        /
                    </span>
                    <span className="text-[var(--cpm-brand-navy)]" style={{ fontWeight: 800 }}>
                        Central Product Master
                    </span>
                </div>
                <div className="flex flex-col" style={{ gap: 24 }}>
                    <div className="flex items-center" style={{ gap: 10, padding: 2 }}>
                        <span className="cpm-spin" style={{ flex: 'none' }} />
                        <span
                            className="uppercase text-[var(--cpm-text-tertiary)]"
                            style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em' }}
                        >
                            Loading the catalog dashboard
                        </span>
                        {slowLoad ? (
                            <span
                                className="text-[var(--cpm-warning-text-med)]"
                                style={{ fontSize: 12, fontWeight: 700 }}
                            >
                                Still loading — aggregating master and link records.
                            </span>
                        ) : null}
                    </div>
                    <span className="cpm-sk" style={{ height: 150, borderRadius: 16 }} />
                    <div
                        className="grid"
                        style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 16 }}
                    >
                        {Array.from({ length: 4 }, (_, i) => (
                            <span
                                key={i}
                                className="cpm-sk"
                                style={{ height: 96, borderRadius: 16 }}
                            />
                        ))}
                    </div>
                    <div
                        className="grid"
                        style={{ gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 16 }}
                    >
                        {Array.from({ length: 3 }, (_, i) => (
                            <span
                                key={i}
                                className="cpm-sk"
                                style={{ height: 300, borderRadius: 16 }}
                            />
                        ))}
                    </div>
                    <div
                        className="grid"
                        style={{
                            gridTemplateColumns: 'minmax(0,1.15fr) minmax(0,1fr)',
                            gap: 16,
                        }}
                    >
                        <span className="cpm-sk" style={{ height: 420, borderRadius: 16 }} />
                        <span className="cpm-sk" style={{ height: 420, borderRadius: 16 }} />
                    </div>
                    <div
                        className="grid"
                        style={{ gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 16 }}
                    >
                        {Array.from({ length: 3 }, (_, i) => (
                            <span
                                key={i}
                                className="cpm-sk"
                                style={{ height: 150, borderRadius: 16 }}
                            />
                        ))}
                    </div>
                </div>
            </CpmPageShell>
        );
    }

    if (overview.loading === 'error' && !stats) {
        return (
            <CpmPageShell gap={24} maxWidth={1280} variant="standard">
                <div
                    className="flex items-center text-[var(--cpm-text-secondary)]"
                    style={{ gap: 8, fontSize: 12.5, fontWeight: 600 }}
                >
                    <span>Admin</span>
                    <span className="text-[var(--cpm-text-tertiary)]" style={{ fontWeight: 100 }}>
                        /
                    </span>
                    <span>Catalog</span>
                    <span className="text-[var(--cpm-text-tertiary)]" style={{ fontWeight: 100 }}>
                        /
                    </span>
                    <span className="text-[var(--cpm-brand-navy)]" style={{ fontWeight: 800 }}>
                        Central Product Master
                    </span>
                </div>
                <div
                    className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-shortfall-border)] rounded-[16px] shadow-[var(--cpm-shadow-card)] flex flex-col items-center text-center"
                    style={{ gap: 14, padding: '44px 32px' }}
                >
                    <span
                        className="inline-flex items-center justify-center text-[var(--cpm-shortfall-text)]"
                        style={{
                            width: 72,
                            height: 72,
                            borderRadius: 18,
                            background: 'var(--cpm-shortfall-bg)',
                            border: '1px solid var(--cpm-shortfall-border)',
                        }}
                    >
                        <svg
                            width="32"
                            height="32"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.9"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <circle cx="12" cy="12" r="10" />
                            <path d="m15 9-6 6" />
                            <path d="m9 9 6 6" />
                        </svg>
                    </span>
                    <h2 className="m-0 !text-[21px] !font-extrabold !tracking-[-0.02em] !text-[var(--cpm-brand-navy)]">
                        Couldn't load the catalog dashboard
                    </h2>
                    <p className="m-0 max-w-[460px] !text-[13.5px] !font-semibold !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                        {overview.error ||
                            'The catalog service did not respond. No data has changed — retry to load the dashboard again.'}
                    </p>
                    <CpmButton
                        variant="navy"
                        size="md"
                        style={{ marginTop: 4 }}
                        disabled={bootRetrying}
                        leadingIcon={bootRetrying ? <span className="cpm-spin-w" /> : undefined}
                        onClick={() => {
                            setBootRetrying(true);
                            dispatch(fetchOverviewStats());
                        }}
                    >
                        {bootRetrying ? 'Retrying…' : 'Retry'}
                    </CpmButton>
                </div>
            </CpmPageShell>
        );
    }

    if (!stats) return null;

    const sec = stats.sections ?? {};
    const sectionFailed = (key: keyof typeof sec) => sec[key] === 'error';

    const linksOk = !sectionFailed('productLinks');
    const brandsOk = !sectionFailed('alcoholBrands');
    const productsOk = !sectionFailed('alcoholProducts');
    const variantsOk = !sectionFailed('productVariants');
    const healthOk = brandsOk || productsOk || variantsOk;
    const barcodeOk = !sectionFailed('barcodeConflicts');
    const batchJobsOk = !sectionFailed('batchJobs');
    const exciseOk = !sectionFailed('exciseReadiness');
    const attentionOk = !sectionFailed('needsAttention');

    const linkNeedsReview = linksOk ? (stats.productLinks.byStatus?.NEEDS_REVIEW ?? 0) : 0;
    const pendingMasters =
        (brandsOk ? (stats.alcoholBrands.byStatus?.PENDING ?? 0) : 0) +
        (productsOk ? (stats.alcoholProducts.byStatus?.PENDING ?? 0) : 0) +
        (variantsOk ? (stats.productVariants.byStatus?.PENDING ?? 0) : 0);
    const queueWaiting =
        linksOk && healthOk && barcodeOk
            ? linkNeedsReview + pendingMasters + (stats.barcodeConflictsCount ?? 0)
            : null;
    const firstUncurated =
        stats.exciseCategoryReadiness.states.find((s) => s.isLive && !s.isCurated)?.name ?? null;
    const softRefreshing = overview.loading === 'loading';
    const catalogEmpty =
        healthOk &&
        stats.heroTotal === 0 &&
        linkNeedsReview === 0 &&
        (stats.barcodeConflictsCount ?? 0) === 0 &&
        (stats.needsAttentionTotalCount ?? 0) === 0;

    const failedJobs =
        (stats.openBatchJobs ?? []).filter((j) =>
            String(j.status || '')
                .toUpperCase()
                .includes('FAIL')
        ).length +
        (stats.needsAttention ?? []).filter((a) => a.type === CPM_ATTENTION_TYPE.BATCH_JOB_FAILED)
            .length;
    const runningJobs =
        (stats.batchJobHealth?.running ?? 0) + (stats.batchJobHealth?.submittedNotStarted ?? 0);
    let pipeline: PipelineBadge = 'idle';
    if (!batchJobsOk) pipeline = 'unknown';
    else if (failedJobs > 0) pipeline = 'attention';
    else if (runningJobs > 0) pipeline = 'live';

    const asOfLabel = formatAsOfLabel(overview.lastFetchedAt);
    const refreshFailedLabel =
        overview.lastFetchedAt != null
            ? `Couldn't refresh — showing ${formatAsOfTime(overview.lastFetchedAt)}`
            : "Couldn't refresh";

    return (
        <CpmPageShell gap={24} maxWidth={1280} variant="standard">
            <div
                className="flex items-center text-[var(--cpm-text-secondary)]"
                style={{ gap: 8, fontSize: 12.5, fontWeight: 600 }}
            >
                <span>Admin</span>
                <span className="text-[var(--cpm-text-tertiary)]" style={{ fontWeight: 100 }}>
                    /
                </span>
                <span>Catalog</span>
                <span className="text-[var(--cpm-text-tertiary)]" style={{ fontWeight: 100 }}>
                    /
                </span>
                <span className="text-[var(--cpm-brand-navy)]" style={{ fontWeight: 800 }}>
                    Central Product Master
                </span>
            </div>

            <Hero
                heroTotal={stats.heroTotal}
                asOfLabel={asOfLabel}
                refreshing={softRefreshing}
                refreshFailed={overview.refreshFailed}
                refreshFailedLabel={refreshFailedLabel}
                pipeline={pipeline}
                onRefresh={handleManualRefresh}
            />

            {catalogEmpty ? (
                <div
                    className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] rounded-[16px] shadow-[var(--cpm-shadow-card)] flex flex-col items-center text-center"
                    style={{ gap: 12, padding: '40px 28px' }}
                >
                    <span
                        className="inline-flex items-center justify-center text-[var(--cpm-brand-purple)]"
                        style={{
                            width: 52,
                            height: 52,
                            borderRadius: 14,
                            background: 'var(--cpm-purple-tint-13)',
                        }}
                    >
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <path d="m7.5 4.27 9 5.15" />
                            <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
                            <path d="m3.3 7 8.7 5 8.7-5" />
                            <path d="M12 22V12" />
                        </svg>
                    </span>
                    <h2 className="m-0 !text-[19px] !font-extrabold !tracking-[-0.02em] !text-[var(--cpm-brand-navy)]">
                        No master records yet
                    </h2>
                    <p className="m-0 max-w-[420px] !text-[13px] !font-semibold !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                        The catalog is empty. Once brands, products, or variants are created — or
                        tenant links arrive — they will show up here.
                    </p>
                    <CpmButton
                        variant="navy"
                        size="md"
                        style={{ marginTop: 4 }}
                        disabled={softRefreshing}
                        leadingIcon={softRefreshing ? <span className="cpm-spin-w" /> : undefined}
                        onClick={() => dispatch(fetchOverviewStats())}
                    >
                        {softRefreshing ? 'Checking…' : 'Check again'}
                    </CpmButton>
                </div>
            ) : null}

            {attentionOk ? (
                <AttentionStrip
                    items={stats.needsAttention}
                    totalCount={stats.needsAttentionTotalCount}
                    expanded={overview.attentionExpanded}
                    onToggleExpand={() => dispatch(toggleAttentionExpanded())}
                />
            ) : (
                <SectionError
                    title="Couldn't load attention items"
                    body="One or more alert sources did not respond. The rest of the dashboard may still be current."
                    onRetry={() => retrySection('needsAttention')}
                    retrying={sectionRetrying === 'needsAttention'}
                />
            )}

            <div className="flex flex-col" style={{ gap: 12 }}>
                <div className="flex items-baseline" style={{ gap: 10 }}>
                    <h2 className="m-0 !text-[14px] !font-extrabold !text-[var(--cpm-text-primary)]">
                        Catalog health
                    </h2>
                    <span
                        className="text-[var(--cpm-text-secondary)]"
                        style={{ fontSize: 12.5, fontWeight: 600 }}
                    >
                        Records in the master catalog, by review status
                    </span>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3" style={{ gap: 16 }}>
                    <CatalogHealthCard
                        title="Brands"
                        icon={<BrandIcon />}
                        data={stats.alcoholBrands}
                        error={!brandsOk}
                        onRetry={() => retrySection('alcoholBrands')}
                        retrying={sectionRetrying === 'alcoholBrands'}
                        editorHref={(status) =>
                            `/central-product-master/master-editor?level=brands&status=${status}`
                        }
                    />
                    <CatalogHealthCard
                        title="Products"
                        icon={<ProductIcon />}
                        data={stats.alcoholProducts}
                        error={!productsOk}
                        onRetry={() => retrySection('alcoholProducts')}
                        retrying={sectionRetrying === 'alcoholProducts'}
                        editorHref={(status) =>
                            `/central-product-master/master-editor?level=catalog&view=products&status=${status}`
                        }
                    />
                    <CatalogHealthCard
                        title="Variants"
                        icon={<VariantIcon />}
                        data={stats.productVariants}
                        error={!variantsOk}
                        onRetry={() => retrySection('productVariants')}
                        retrying={sectionRetrying === 'productVariants'}
                        editorHref={(status) =>
                            `/central-product-master/master-editor?level=catalog&view=variants&status=${status}`
                        }
                    />
                </div>
                <div
                    className="flex items-center rounded-[10px] bg-[var(--cpm-info-bg)] border border-[var(--cpm-info-border)]"
                    style={{ gap: 9, padding: '11px 16px' }}
                >
                    <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--cpm-info-icon)"
                        strokeWidth="2"
                        className="shrink-0"
                    >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 16v-4" />
                        <path d="M12 8h.01" />
                    </svg>
                    <span
                        className="text-[var(--cpm-info-text)]"
                        style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.45 }}
                    >
                        A healthy catalog settles into mostly Active/verified over time — a small,
                        steady Pending review share is normal day-to-day review, not a growing
                        backlog. Watch the trend, not the absolute number.
                    </span>
                </div>
            </div>

            <div
                className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] items-stretch"
                style={{ gap: 16 }}
            >
                <LinkDecisionsPanel
                    productLinks={stats.productLinks}
                    barcodeConflictsCount={stats.barcodeConflictsCount}
                    linksError={!linksOk}
                    barcodeError={!barcodeOk}
                    onRetryLinks={() => retrySection('productLinks')}
                    onRetryBarcode={() => retrySection('barcodeConflicts')}
                    retryingLinks={sectionRetrying === 'productLinks'}
                    retryingBarcode={sectionRetrying === 'barcodeConflicts'}
                />
                <TenantLinkagePanel
                    tenantLinkage={stats.tenantLinkage}
                    linkNeedsReview={linkNeedsReview}
                    infoOpen={overview.tenantInfoOpen}
                    onToggleInfo={() => dispatch(toggleTenantInfo())}
                    error={sectionFailed('tenantLinkage')}
                    onRetry={() => retrySection('tenantLinkage')}
                    retrying={sectionRetrying === 'tenantLinkage'}
                />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 items-start" style={{ gap: 16 }}>
                <ExciseReadinessPanel
                    readiness={stats.exciseCategoryReadiness}
                    stateProductByState={stateProductByState}
                    infoOpen={overview.exciseInfoOpen}
                    onToggleInfo={() => dispatch(toggleExciseInfo())}
                    error={!exciseOk}
                    onRetry={() => retrySection('exciseReadiness')}
                    retrying={sectionRetrying === 'exciseReadiness'}
                />
                <BatchJobHealthPanel
                    batchJobHealth={stats.batchJobHealth}
                    openBatchJobs={stats.openBatchJobs}
                    confirmingJobId={overview.confirmingJobId}
                    retriedJobIds={overview.retriedJobIds}
                    onAskRetry={(id) => dispatch(setConfirmingJobId(id))}
                    onCancelRetry={() => dispatch(setConfirmingJobId(null))}
                    onConfirmRetry={handleConfirmRetry}
                    retrying={retrying}
                    error={!batchJobsOk}
                    onRetryLoad={() => retrySection('batchJobs')}
                    retryingLoad={sectionRetrying === 'batchJobs'}
                />
            </div>

            <QuickLinksGrid
                queueWaiting={queueWaiting}
                heroTotal={healthOk ? stats.heroTotal : null}
                uncuratedStates={exciseOk ? stats.exciseCategoryReadiness.uncuratedCount : null}
                uncuratedStateName={firstUncurated}
            />
        </CpmPageShell>
    );
}
